'use client';

import { useDraggableScroll } from '@tuic/lib/hooks/use-draggable-scroll';
import { useMediaQuery } from '@tuic/lib/hooks/use-media-query';
import { logger } from '@tuic/logger';
import { twMerge } from '@tuic/tailwind-config';
import debounce from 'lodash/debounce';
import {
  useState,
  Children,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

import { useScreenName } from '../../../hooks/use-screen-name';

import { CarouselContext } from './CarouselContext';
import {
  combineEventHandlers,
  createToIndexScroller,
  getChildrenAsArrayOfElements,
  getVisibleIndexes,
} from './helpers';

import type {
  FC,
  HTMLAttributes,
  MouseEventHandler,
  ReactElement,
  ReactNode} from 'react';

type Props = {
  children: ReactNode;

  /**
   * Determines if carousel items can be dragged with a mouse. Defaults to false. On touch devices, dragging is enabled by default.
   */
  draggable?: boolean;

  /**
   * Props applied to each li element wrapping individual carousel items. Ideal for consistent styling or item spacing.
   */
  itemProps?: HTMLAttributes<HTMLLIElement>;
} & HTMLAttributes<HTMLUListElement>;

/**
 * CarouselStage Component
 *
 * Encapsulates carousel items within a `ul` element, systematically wrapping each item in a `li`.
 * Designed to be used exclusively within the `Carousel` component for smooth and customizable scrolling experiences.
 *
 * ### Example
 * ```tsx
 * <UniversalCarousel>
 *   <CarouselStage>
 *     <Image src="..." className="aspect-4/3" />
 *     <Image src="..." className="aspect-4/3" />
 *     <Image src="..." className="aspect-4/3" />
 *   </CarouselStage>
 * </UniversalCarousel>
 * ```
 *
 * ## Props
 * - `draggable` (boolean): Enable or disable mouse dragging for the carousel. Defaults to `false`. On touch devices, dragging is always enabled.
 * - `itemProps` (object): Props applied to each `li` element wrapping the carousel items, ideal for consistent styling or spacing.
 *
 * ### Adjusting Item Width
 * Use the `carousel-item-w-*` utilities to set the width of carousel items.
 * Items with width set as percentages (e.g., `w-1/3`, `w-[50%]`) automatically account for gaps specified by the `carousel-stage-gap` utility.
 *
 * ```tsx
 * <CarouselStage itemProps={{ className: 'carousel-item-w-1/2' }} />
 * // Sets each item's width to 50% of the carousel width.
 *
 * <CarouselStage itemProps={{ className: 'carousel-item-w-[80%]' }} />
 * // Each item occupies 80% of the width, partially exposing the next item.
 * ```
 *
 * ### Managing Spacing Between Items
 *
 * - **Single Visible Item**: Use `margin` utilities to add space between elements.
 *   ```tsx
 *   <CarouselStage itemProps={{ className: 'mr-2 last:mr-0' }} />
 *   ```
 *
 * - **Multiple Visible Items**: Use `carousel-stage-gap` utilities to add gaps. These gaps are automatically considered when using `carousel-item-w`.
 *   ```tsx
 *   <CarouselStage className="carousel-stage-gap-2" itemProps={{ className: 'carousel-item-w-1/3' }} />
 *   ```
 */
export const CarouselStage: FC<Props> = ({
  children,
  draggable = true,
  className: propClassName,
  onScroll: propOnScroll,
  onMouseDown: propOnMouseDown,
  itemProps,
  ...rest
}) => {
  const { set: setContextValue, enabled } = useContext(CarouselContext);
  const ref = useRef<HTMLUListElement>(null);
  const screenName = useScreenName();

  const [dragging, setDragging] = useState<boolean>(false);
  const [preventClick, setPreventClick] = useState<boolean>(false);
  const dragStartPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Create a debounced function that updates the current index.
  const updateCurrentIndexDebounced = useRef(
    debounce(() => {
      setContextValue(prev => ({
        ...prev,
        visibleIndexes: getVisibleIndexes(ref),
        stageScrollLeft: ref.current?.scrollLeft ?? 0,
        stageScrollWidth: ref.current?.scrollWidth ?? 0,
        stageWidth: ref.current?.offsetWidth ?? 0,
      }));
    }, 100),
  );

  // Set up the draggable scroll on desktop devices with a fine pointer (a mouse).
  const hasFinePointer = useMediaQuery('pointer: fine');
  const isDraggable = enabled && (draggable || !hasFinePointer); // Enable dragging on touch devices

  const { onMouseDown: onMouseDownDraggable, onScroll: onScrollDraggable } =
    useDraggableScroll(ref, {
      direction: 'horizontal',
      enabled: isDraggable,
      onDragStart: ev => {
        setPreventClick(false);
        dragStartPosition.current = { x: ev.clientX, y: ev.clientY };
      },
      onDrag: (dx, dy) => {
        const distanceMoved = Math.hypot(dx, dy); // Use dx and dy
        if (distanceMoved > 5) {
          setPreventClick(true); // Suppress clicks if drag movement exceeds 5px
        }
      },
      onDragEnd: () => {
        updateCurrentIndexDebounced.current();

        // Conditionally set scroll-snap-type only if it's not already 'x mandatory'
        ref.current?.style.setProperty('scroll-snap-type', 'x mandatory');

        // Reset preventClick after a short delay
        setTimeout(() => setPreventClick(false), 100);
      },
    });

  // Get the children as an array of (react) elements.
  let childrenAsArray: ReturnType<typeof getChildrenAsArrayOfElements> = [];
  try {
    childrenAsArray = getChildrenAsArrayOfElements(children);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      throw error;
    }
    logger.warn('[CarouselStage] Could not resolve react children', children);
  }
  const childrenCount = childrenAsArray.length;

  // Merge the classNames of the wrapping list.
  const containerClassName = twMerge(
    enabled && 'snap-x snap-mandatory [-webkit-overflow-scrolling:touch]',
    propClassName,
    enabled && 'relative flex overflow-x-auto scrollbar-hidden',
    'touch-manipulation', // Enable both horizontal and vertical touch actions
  );

  // Merge the classNames of the list items.
  const itemClassName = twMerge(
    enabled && 'carousel-item-w-full',
    enabled && !itemProps?.className?.includes('snap-') && 'snap-start',
    itemProps?.className,
    enabled && 'shrink-0',
    dragging && 'pointer-events-none',
  );

  // Update the context value when the children change.
  useEffect(() => {
    const visibleIndexes = getVisibleIndexes(ref);
    const scrollToIndex = createToIndexScroller(ref, childrenCount - 1);

    setContextValue(prev => ({
      ...prev,
      lastIndex: childrenCount - 1,
      visibleIndexes,
      visibleItems: visibleIndexes.length,
      scrollToIndex,
      stageWidth: ref.current?.offsetWidth ?? 0,
      stageScrollWidth: ref.current?.scrollWidth ?? 0,
    }));

    // Scroll to the first item on mount or children change
    scrollToIndex(0, 'start', 'instant');
  }, [enabled, childrenCount, setContextValue, screenName]);

  // Combine the event handlers which are required for the draggable scroll.
  const handleMouseDown = useCallback<MouseEventHandler<HTMLUListElement>>(
    ev => {
      if (!ref.current?.contains(ev.target as Node)) {
        return; // Ignore the event if the target is not within the CarouselStage
      }
      combineEventHandlers([onMouseDownDraggable, propOnMouseDown])(ev);
    },
    [onMouseDownDraggable, propOnMouseDown],
  );

  const handleScroll = useCallback<MouseEventHandler<HTMLUListElement>>(
    ev => {
      combineEventHandlers([
        onScrollDraggable,
        updateCurrentIndexDebounced.current,
        propOnScroll,
      ])(ev);
    },
    [onScrollDraggable, propOnScroll],
  );

  return (
    <ul
      {...rest}
      className={containerClassName}
      ref={ref}
      onMouseDown={handleMouseDown}
      onScroll={handleScroll}
    >
      {Children.map<ReactNode, ReactElement>(childrenAsArray, (child, idx) => (
        <li
          {...itemProps}
          key={child.key ?? idx}
          className={itemClassName}
          onClick={ev => {
            if (preventClick) {
              ev.preventDefault(); // Suppress the click if a drag occurred
              return;
            }
          }}
        >
          {child}
        </li>
      ))}
    </ul>
  );
};
